import React, { useMemo } from "react";
import { useStores } from "_common/hooks";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { NotificationManager } from "react-notifications";
import { PaymentApi } from "states/api/payment";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { formatBalance, formatNumber } from "_common/utils/formatValue";
import i18next from "i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLongArrowAltLeft,
  faChevronRight,
  faClipboardList,
} from "@fortawesome/free-solid-svg-icons";

const schema = yup
  .object({
    address: yup.string(),
    amount: yup.number().required(i18next.t("verify_require")),
    wallet_password: yup.string(),
  })
  .required();

export default function WithdrawSubmit() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const symbol = params.get("symbol") || "usdt";

  const {
    authStore: { user, general, coins, bank_select },
  } = useStores();

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isValid, isSubmitted },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const amount = watch("amount");

  const mainCoin = useMemo(
    () => coins?.find((obj) => obj?.symbol === symbol),
    [coins, symbol]
  );

  const mainBalance = useMemo(
    () => user?.tokens?.find((obj) => obj?.symbol === symbol),
    [user, symbol]
  );

  const { mutate: onSubmit, isLoading } = useMutation(
    (variables) =>
      PaymentApi.withdraw({
        params: {
          ...variables,
          symbol,
          bank_id: bank_select?.id,
          type: "withdraw",
        },
      }),
    {
      onSuccess: (res) => {
        reset();
        queryClient.invalidateQueries(["get_profile"]);
        queryClient.invalidateQueries(["payment", "list"]);
        NotificationManager.success(
          i18next.t(res?.msg) || i18next.t("withdrawal_successful")
        );
      },
      onError: (error) => {
        const errorMessage =
          i18next.t(error?.message) ?? i18next.t("action_failed_msg");
        NotificationManager.error(errorMessage);
      },
    }
  );

  const onSave = (values) => {
    if (isLoading) return null;
    const amount = values?.amount;

    if (amount < Number(general?.min_withdraw || 0))
      return NotificationManager.error(
        `${i18next.t("enter_large_amount_msg")} ${formatNumber(
          general?.min_withdraw
        )}`
      );
    if (amount > Number(general?.max_withdraw || 0))
      return NotificationManager.error(
        `${i18next.t("enter_smaller_amount_msg")} ${formatNumber(
          general?.max_withdraw
        )}`
      );
    // if (!values?.wallet_password)
    //   return NotificationManager.error("Vui lòng nhập mật khẩu rút tiền");
    onSubmit({ ...values });
    return null;
  };

  return (
    <div className="container">
      <div className="topbox flex justify-between items-center">
        <span className="btn_back" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faLongArrowAltLeft} color="#707A8A" />
        </span>
        <div
          className="fr allhg txtr w-[10%] leading-[40px]"
          onClick={() => navigate("/board/history")}
        >
          <svg
            viewBox="0 0 1024 1024"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            className="inline-block"
          >
            <path
              d="M914.9 158.4H183.5c-15.2 0-27.6-12.4-27.6-27.6v-4.1c0-15.2 12.4-27.6 27.6-27.6h731.4c15.2 0 27.6 12.4 27.6 27.6v4.1c0.1 15.2-12.3 27.6-27.6 27.6zM914.9 819.9H183.5c-15.2 0-27.6-12.4-27.6-27.6v-4.1c0-15.2 12.4-27.6 27.6-27.6h731.4c15.2 0 27.6 12.4 27.6 27.6v4.1c0.1 15.2-12.3 27.6-27.6 27.6zM574.7 489.2H176.6c-11.4 0-20.7-9.3-20.7-20.7v-18.1c0-11.4 9.3-20.7 20.7-20.7h398.1c11.4 0 20.7 9.3 20.7 20.7v18.1c0 11.4-9.3 20.7-20.7 20.7z"
              fill="#00b897"
            />
          </svg>
        </div>
      </div>
      <div id="withdraw" className="pt-[50px] px-[15px]">
        <div className="contentbox_top">
          <span className="fe6im text-[18px] text-[500]">Rút</span>
        </div>
        <div className="contentbox_line">
          <div className="line_1">
            <span className="fzmmm fe6im">Cặp tiền</span>
          </div>
          <div className="line_2" onClick={() => navigate(-1)}>
            <b className="text-[18px] text-[#00b897] text-uppercase">
              {symbol}
            </b>
            <span className="fzmmm fe6im mr-[5px]">
              &nbsp;&nbsp;{mainCoin?.name}
            </span>
            <FontAwesomeIcon icon={faChevronRight} color="#707A8A" />
          </div>
        </div>
        <form className="imgbox" onSubmit={handleSubmit(onSave)}>
          <div className="imgbox_4 h-[370px] !border-[0] !mt-[0]">
            <div className="imgbox_1 mt-[10px]">
              <span className="fzmmm fe6im">Số tài khoản</span>
            </div>
            <div className="tx_inputbox">
              <div className="tx_inputbox_l">
                <input
                  type="text"
                  className="w-full h-[30px] bg-[#e6e6e6] border-[1px] border-[#e6e6e6]"
                  placeholder="Nhập hoặc nhấn dài địa chỉ dán"
                  readOnly
                  value={`${bank_select?.bank_name}(${bank_select?.bank_number_display})`}
                />
              </div>
              <div className="tx_inputbox_r">
                <FontAwesomeIcon
                  icon={faClipboardList}
                  width="22"
                  color="#444545"
                />
              </div>
            </div>
            <div className="imgbox_1 mt-[10px] flex justify-between">
              <div className="h-[40px] leading-[40px]">
                <span className="fzmmm fe6im">Số tiền rút</span>
              </div>
              <div className="h-[40px] leading-[40px] text-right">
                <span className="fzmmm fe6im">Có sẵn&nbsp;&nbsp;</span>
                <span className="fzmmm fe6im">
                  {formatBalance(user?.balance)}&nbsp;&nbsp;
                </span>
                <span className="fzmmm fe6im">USDT</span>
              </div>
            </div>
            <div className="tx_inputbox">
              <div className="tx_inputbox_l !w-[65%]">
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={0.000001}
                  autoComplete="off"
                  className="w-full h-[30px] bg-[#e6e6e6] border-[1px] border-[#e6e6e6]"
                  placeholder={i18next.t("coins_withdraw_msg")}
                  {...register("amount")}
                />
              </div>
              <div className="tx_inputbox_r !w-[35%]">
                <div className="inputbox_r_l">
                  <span className="fzmmm fcc !text-[14px]">
                    {mainCoin?.name}
                  </span>
                </div>
                <div className="inputbox_r_r">
                  <span
                    className="fzmmm fcc !text-[14px]"
                    onClick={() => {
                      setValue("amount", Number(user?.balance));
                    }}
                  >
                    Toàn bộ
                  </span>
                </div>
              </div>
            </div>
            {/* <div className="imgbox_1 mt-[10px]">
              <span className="fzmmm fe6im">Mật khẩu rút tiền</span>
            </div>
            <div className="tx_inputbox">
              <div className="tx_inputbox_l">
                <input
                  type="password"
                  placeholder="Mật khẩu rút tiền"
                  className="w-full h-[30px] text-[#707a8a] bg-[#e6e6e6] border-[1px] border-[#e6e6e6]"
                  {...register("wallet_password")}
                />
              </div>
            </div> */}
            <div className="imgbox_8 !h-[unset] mt-[20px]">
              <div className="imgbox_10 !bg-[#00b897]" id="sumbtn">
                <button type="submit" className="fzmm fe6im w-full">
                  Gửi đi
                </button>
              </div>
            </div>
            <div className="imgbox_1 h-[40px] flex justify-between items-center mt-4">
              <div className="w-[50%] h-[40px] leading-[40px]">
                <span className="fzmmm fe6im">Phí giao dịch (3%)</span>
              </div>
              <div className="w-[50%] h-[40px] leading-[40px] text-right">
                <span className="fzmmm fe6im">
                  {formatBalance(amount * 0.03)}
                </span>
                <span className="fzmmm fe6im">&nbsp;&nbsp;USDT</span>
              </div>
            </div>
            <div className="imgbox_1 h-[40px] flex justify-between items-center">
              <div className="w-[50%] h-[40px] leading-[40px]">
                <span className="fzmmm fe6im">Số tiền ước tính</span>
              </div>
              <div className="w-[50%] h-[40px] leading-[40px] text-right">
                <span className="fzmmm fe6im">
                  {formatBalance(amount * 0.97)}
                </span>
                <span className="fzmmm fe6im">&nbsp;&nbsp;USDT</span>
              </div>
            </div>
          </div>
        </form>
        <div className="textbox">
          <p className="fzm text-red">
            Số tiền tối thiểu：{formatNumber(general?.min_withdraw)} USDT，Số
            tiền tối thiểu ít hơn sẽ không nhận được tài khoản và không thể được
            trả lại
          </p>
          <p className="fzm fe6im">
            Để đảm bảo bảo mật tiền, khi chiến lược bảo mật tài khoản của bạn
            được thay đổi và mật khẩu được sửa đổi, chúng tôi sẽ tiến hành xem
            xét thủ công hóa đơn thanh toán. Vui lòng kiên nhẫn chờ đợi điện
            thoại hoặc email của nhân viên.
          </p>
          <p className="fzm fe6im">
            Vui lòng xác nhận bảo mật của máy tính và trình duyệt để ngăn thông
            tin bị giả mạo hoặc bị rò rỉ
          </p>
        </div>
      </div>
    </div>
  );
}
